import { useMutation } from '@apollo/client';
import { Loader2, RefreshCw } from 'lucide-react';
import { useState } from 'react';

import { REFRESH_INVESTMENT_COMPLIANCE } from '@/apollo/schemas/investments/refreshInvestmentCompliance';

import type { TInvestment } from './InvestmentsTableColumns';

type Compliance = NonNullable<TInvestment['compliance']>;

/**
 * The "Mint ready" cell, with a "Check now" for anything not ready.
 *
 * Investor onboarding finishes on its own (wallet creation starts it, a job every minute
 * completes it), so this is never required — it is for an admin about to approve who does not
 * want to wait for the next run, or for a wallet that failed before that pipeline existed.
 * The result replaces the row's status in place. It cannot hurry Chainlink: a freshly issued
 * ACE credential still takes ~10-15 minutes to go on-chain, and "Verifying" says so.
 */
export const ComplianceCell = ({ investment }: { investment: TInvestment }) => {
  const [compliance, setCompliance] = useState<Compliance | null | undefined>(
    investment.compliance,
  );
  const [note, setNote] = useState<string | null>(null);
  const [refresh, { loading }] = useMutation(REFRESH_INVESTMENT_COMPLIANCE);

  // Not every investment is waiting on a mint, and older responses omit the field.
  // Both are "no opinion", which must look different from "blocked".
  if (!compliance || !compliance.applicable) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }

  if (compliance.ready) {
    return (
      <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
        Ready
      </span>
    );
  }

  const checkNow = async () => {
    setNote(null);
    try {
      const { data } = await refresh({
        variables: { investmentId: investment.id },
      });
      const res = data?.refreshInvestmentCompliance;
      if (res?.statusCode === 200 && res.compliance) {
        setCompliance(res.compliance);
        setNote(res.message);
      } else {
        setNote(res?.message ?? 'Could not check compliance.');
      }
    } catch (err: any) {
      setNote(err?.message ?? 'Could not check compliance.');
    }
  };

  // COMPLIANCE_PENDING clears on its own; the others do not. Colour carries that
  // distinction so a queue can be triaged at a glance.
  const waiting = compliance.blockedStatus === 'COMPLIANCE_PENDING';
  const label = waiting ? 'Verifying' : 'Blocked';
  const tone = waiting
    ? 'border-amber-200 bg-amber-50 text-amber-700'
    : 'border-rose-200 bg-rose-50 text-rose-700';

  return (
    <div className="max-w-[240px]">
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${tone}`}
          title={compliance.reason ?? undefined}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={checkNow}
          disabled={loading}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
          title="Finish any pending compliance step for this investor now"
        >
          {loading ? (
            <Loader2 size={11} className="animate-spin" />
          ) : (
            <RefreshCw size={11} />
          )}
          Check now
        </button>
      </div>
      {compliance.reason && (
        <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
          {compliance.reason}
        </p>
      )}
      {note && (
        <p className="mt-1 text-[11px] leading-snug text-foreground">{note}</p>
      )}
    </div>
  );
};
